import { useMemo } from "react";
import { AlertCircle, Package, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import Button from "../UI/Button";
import { Input, Select } from "../UI/FormField";
import { SectionCard } from "../UI/SectionCard";
import { CONTAINER_CARGO_TYPES } from "../../constants/containerOrderConstants";
import {
  emptyCargoRow,
  findCargoRowErrors,
  numOrNull,
} from "../../utils/containerLineCargo";
import type {
  CargoRequiredField,
  CargoRowDraft,
  WeightIssue,
} from "../../utils/containerLineCargo";

/** The asterisk on a required column heading. */
const Required = () => (
  <span aria-hidden className="text-[#C97A3C]">
    *
  </span>
);

const sum = (rows: CargoRowDraft[], field: keyof CargoRowDraft) =>
  rows.reduce((total, row) => total + (numOrNull(row[field] as string) ?? 0), 0);


interface ContainerLineCargoSectionProps {
  rows: CargoRowDraft[];
  onChange: (rows: CargoRowDraft[]) => void;
  /** Cancelled or completed container — shown, not edited. */
  disabled?: boolean;
  /**
   * Turned on by a rejected save. Errors stay hidden until then so the form
   * does not go red under someone who is still halfway through typing the row.
   */
  showErrors?: boolean;
  /**
   * What this container may carry — VGM minus tare — or null while either half
   * is unknown. Passed in rather than derived here because the weights live on
   * the container, not on its packing list; the totals bar is simply the place
   * the sum is already being added up, so it is the honest place to say whether
   * the sum fits.
   */
  allowanceKg?: number | null;
  /**
   * The weight problem this packing list is currently causing, decided by the
   * container's own evaluator and passed down.
   *
   * Passed rather than recomputed because the answer depends on figures this
   * table cannot see: an allowance derived from a VGM that is itself below the
   * tare is a negative number, and a table that only knew the allowance would
   * announce the packing list as overweight for a mistake made two fields away.
   */
  weightIssue?: WeightIssue | null;
}

/**
 * Cargo Information for one container.
 *
 * Deliberately not frozen once the run starts, unlike the container's own
 * number and seal: the packing list is finalised at the loading site,
 * which is stop 2 of 3. Refusing the edit that records what was actually loaded
 * would be refusing the only edit that matters at that point.
 *
 * It looks like the shipment form's cargo table because both transcribe the
 * same packing list, but the two modules share no code and no state — see the
 * note on `cargoItemSchema` in the line model. LDM and chargeable weight are
 * absent: a trailer measure and an air-freight billing rule, neither of which
 * describes a sealed box.
 */
export function ContainerLineCargoSection({
  rows,
  onChange,
  disabled = false,
  showErrors = false,
  allowanceKg = null,
  weightIssue = null,
}: ContainerLineCargoSectionProps) {
  const { t } = useTranslation("containerOrders");

  // A locked container is a record, not a form: whatever is stored is shown as
  // it stands, even if it predates the rule.
  const rowErrors = useMemo(
    () => (disabled ? {} : findCargoRowErrors(rows)),
    [disabled, rows],
  );

  const invalid = (key: string, field: CargoRequiredField) =>
    showErrors && (rowErrors[key]?.includes(field) ?? false);

  const typeOptions = useMemo(
    () =>
      CONTAINER_CARGO_TYPES.map((value) => ({
        value,
        label: t(`lineCargo.types.${value}`),
      })),
    [t],
  );

  /**
   * Edit one field of one row.
   *
   * Changing a dimension refills the volume, but only while the volume box is
   * empty or zero — the moment someone types a measured figure, the derived one
   * backs off and stops overwriting it. Clearing the box re-arms the fill.
   * Same bargain the shipment form strikes, reached here without a watcher
   * effect: the recompute happens in the edit that caused it, so it cannot loop.
   */
  const updateRow = (
    key: string,
    field: keyof Omit<CargoRowDraft, "key" | "_id">,
    value: string,
  ) => {
    onChange(
      rows.map((row) => {
        if (row.key !== key) return row;

        const next = { ...row, [field]: value };

        if (field === "length" || field === "width" || field === "height") {
          const derivable = !next.volumeM3.trim() || Number(next.volumeM3) === 0;
          const l = Number(next.length);
          const w = Number(next.width);
          const h = Number(next.height);

          if (derivable && l > 0 && w > 0 && h > 0) {
            next.volumeM3 = String(Number(((l * w * h) / 1_000_000).toFixed(3)));
          }
        }

        return next;
      }),
    );
  };

  const addRow = () => onChange([...rows, emptyCargoRow()]);

  const removeRow = (key: string) =>
    onChange(rows.filter((row) => row.key !== key));

  const totalQuantity = sum(rows, "quantity");
  const totalWeight = sum(rows, "weightKg");
  const totalVolume = sum(rows, "volumeM3");

  // The one figure on this bar that can be wrong rather than merely incomplete:
  // a packing list heavier than what the weighbridge leaves room for, or than
  // the container is rated to carry, describes a load that cannot be dispatched.
  // Shown against the ceiling, so the total reads as "of what" rather than as a
  // number on its own.

  const th =
    "px-2 py-2 text-left text-[10px] font-semibold uppercase tracking-wider text-gray-500";
  const td = "px-2 py-1.5 align-top whitespace-nowrap";

  return (
    <SectionCard
      icon={Package}
      title={t("lineCargo.title")}
      actions={
        !disabled && (
          <Button type="button" variant="primary" onClick={addRow}>
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t("lineCargo.addItem")}
          </Button>
        )
      }
    >
      {weightIssue && (
        <p className="mb-2 flex items-start gap-2 rounded-lg bg-[#FCE9E9] px-3 py-2 text-[12px] text-[#A63A39]">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t(`lineWeights.${weightIssue.code}`, weightIssue.values)}
        </p>
      )}

      {showErrors && Object.keys(rowErrors).length > 0 && (
        <p className="mb-2 flex items-start gap-2 rounded-lg bg-[#FCE9E9] px-3 py-2 text-[12px] text-[#A63A39]">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {t("lineCargo.requiredFields", {
            count: Object.keys(rowErrors).length,
          })}
        </p>
      )}

      {rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-gray-200 px-3 py-6 text-center text-[12px] text-gray-400">
          {t(disabled ? "lineCargo.emptyReadOnly" : "lineCargo.empty")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-md border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="border-b border-gray-200 bg-gray-50 text-nowrap">
                <tr>
                  <th className={th}>
                    {t("lineCargo.columns.qty")} <Required />
                  </th>
                  <th className={th}>
                    {t("lineCargo.columns.type")} <Required />
                  </th>
                  <th className={th}>
                    {t("lineCargo.columns.weightKg")} <Required />
                  </th>
                  <th className={th}>{t("lineCargo.columns.volumeM3")}</th>
                  <th className={th}>
                    {t("lineCargo.columns.dimensions")}{" "}
                    <span className="font-normal normal-case text-gray-400">
                      {t("lineCargo.columns.lxwxh")}
                    </span>
                  </th>
                  <th className={th}>
                    {t("lineCargo.columns.description")} <Required />
                  </th>
                  <th className={th}>{t("lineCargo.columns.marks")}</th>
                  {!disabled && (
                    <th className={th}>{t("lineCargo.columns.action")}</th>
                  )}
                </tr>
              </thead>

              <tbody className="bg-white">
                {rows.map((row) => (
                  <tr
                    key={row.key}
                    className="border-b border-gray-100 last:border-0"
                  >
                    <td className={td}>
                      <Input
                        size="sm"
                        type="number"
                        min={0}
                        step={1}
                        value={row.quantity}
                        onChange={(e) =>
                          updateRow(row.key, "quantity", e.target.value)
                        }
                        disabled={disabled}
                        error={invalid(row.key, "quantity")}
                        style={{ width: "72px" }}
                      />
                    </td>

                    <td className={td}>
                      <Select
                        size="sm"
                        value={row.type}
                        onChange={(e) =>
                          updateRow(row.key, "type", e.target.value)
                        }
                        error={invalid(row.key, "type")}
                        options={typeOptions}
                        placeholder={t("lineCargo.select")}
                        disabled={disabled}
                      />
                    </td>

                    <td className={td}>
                      <Input
                        size="sm"
                        type="number"
                        step="0.01"
                        min={0}
                        value={row.weightKg}
                        onChange={(e) =>
                          updateRow(row.key, "weightKg", e.target.value)
                        }
                        disabled={disabled}
                        error={invalid(row.key, "weightKg")}
                        style={{ width: "90px" }}
                      />
                    </td>

                    <td className={td}>
                      <Input
                        size="sm"
                        type="number"
                        step="0.001"
                        min={0}
                        value={row.volumeM3}
                        onChange={(e) =>
                          updateRow(row.key, "volumeM3", e.target.value)
                        }
                        disabled={disabled}
                        style={{ width: "90px" }}
                      />
                    </td>

                    <td className={td}>
                      <div className="flex space-x-1">
                        <Input
                          size="sm"
                          type="number"
                          min={0}
                          value={row.length}
                          onChange={(e) =>
                            updateRow(row.key, "length", e.target.value)
                          }
                          disabled={disabled}
                          placeholder={t("lineCargo.columns.length")}
                          style={{ width: "54px" }}
                        />
                        <Input
                          size="sm"
                          type="number"
                          min={0}
                          value={row.width}
                          onChange={(e) =>
                            updateRow(row.key, "width", e.target.value)
                          }
                          disabled={disabled}
                          placeholder={t("lineCargo.columns.width")}
                          style={{ width: "54px" }}
                        />
                        <Input
                          size="sm"
                          type="number"
                          min={0}
                          value={row.height}
                          onChange={(e) =>
                            updateRow(row.key, "height", e.target.value)
                          }
                          disabled={disabled}
                          placeholder={t("lineCargo.columns.height")}
                          style={{ width: "54px" }}
                        />
                      </div>
                    </td>

                    <td className={td}>
                      <Input
                        size="sm"
                        value={row.description}
                        onChange={(e) =>
                          updateRow(row.key, "description", e.target.value)
                        }
                        disabled={disabled}
                        error={invalid(row.key, "description")}
                        placeholder={t("lineCargo.columns.description")}
                        style={{ width: "160px" }}
                      />
                    </td>

                    <td className={td}>
                      <Input
                        size="sm"
                        value={row.marks}
                        onChange={(e) =>
                          updateRow(row.key, "marks", e.target.value)
                        }
                        disabled={disabled}
                        placeholder={t("lineCargo.columns.marks")}
                        style={{ width: "140px" }}
                      />
                    </td>

                    {!disabled && (
                      <td className={td}>
                        <Button
                          type="button"
                          variant="ghost"
                          onClick={() => removeRow(row.key)}
                          aria-label={t("lineCargo.removeItem")}
                          title={t("lineCargo.removeItem")}
                          className="p-1 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>

            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 bg-gray-50 px-3 py-2 text-[12px]">
            <span className="font-semibold uppercase tracking-wide text-gray-600">
              {t("lineCargo.totals")}
            </span>
            <div className="flex flex-wrap gap-x-6 gap-y-1">
              <div>
                <span className="text-gray-500">
                  {t("lineCargo.totalQuantity")}
                </span>{" "}
                <span className="font-semibold text-gray-900">
                  {totalQuantity}
                </span>
              </div>
              <div>
                <span className="text-gray-500">
                  {t("lineCargo.totalWeight")}
                </span>{" "}
                <span
                  className={`font-semibold ${
                    weightIssue ? "text-[#A63A39]" : "text-gray-900"
                  }`}
                >
                  {totalWeight.toFixed(2)} kg
                </span>
                {allowanceKg !== null && (
                  <span className="ml-1 text-gray-400">
                    {t("lineCargo.ofAllowance", {
                      allowance: allowanceKg.toLocaleString(),
                    })}
                  </span>
                )}
              </div>
              <div>
                <span className="text-gray-500">
                  {t("lineCargo.totalVolume")}
                </span>{" "}
                <span className="font-semibold text-gray-900">
                  {totalVolume.toFixed(3)}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
